import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import useOtpLogin from '../../hooks/useOtpLogin';

/**
 * EmailOtpForm — the front page's sign-in: your email, then the code from
 * it, then your Kuutio. Drawn in the PIN screen's own style so the two read
 * as one card. Anyone with an active account gets a code, participants
 * included; the backend decides, the form never says who is on the list.
 * `note` replaces the participant wording under the address (/login's is
 * for people given access to a page, #799).
 */

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Input = styled.input`
  width: 100%;
  padding: 16px 20px;
  background: #0a0a0a;
  border: 1px solid ${(p) => (p.$error ? '#ef4444' : '#222')};
  border-radius: 4px;
  color: #fff;
  font-size: ${(p) => (p.$code ? '24px' : '16px')};
  font-family: ${(p) => (p.$code ? "'JetBrains Mono', 'SF Mono', monospace" : 'inherit')};
  text-align: center;
  letter-spacing: ${(p) => (p.$code ? '8px' : 'normal')};
  box-sizing: border-box;
  &:focus { outline: none; border-color: #3949ab; }
  &::placeholder { color: #444; letter-spacing: ${(p) => (p.$code ? '2px' : 'normal')}; }
`;

const Button = styled.button`
  width: 100%;
  padding: 16px;
  background: ${(p) => (p.$ready ? 'linear-gradient(135deg, #1a237e 0%, #4a148c 100%)' : '#1a1a1a')};
  border: 1px solid ${(p) => (p.$ready ? '#3949ab' : '#333')};
  border-radius: 4px;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Note = styled.div`
  font-size: 13px;
  text-align: center;
  color: ${(p) => (p.$tone === 'error' ? '#ef4444' : p.$tone === 'ok' ? '#7bffb2' : '#666')};
  line-height: 1.5;
`;

const TextButton = styled.button`
  background: none;
  border: none;
  color: #888;
  font-size: 13px;
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  &:disabled { opacity: 0.5; cursor: default; }
`;

export default function EmailOtpForm({ redirectTo = '/me', note }) {
  const { t } = useTranslation();
  const otp = useOtpLogin({
    onSuccess: () => window.location.assign(redirectTo),
    resendNotice: t('pinLogin.codeResent'),
    successNotice: t('pinLogin.signingIn'),
  });
  const inputRef = useRef(null);
  useEffect(() => { inputRef.current?.focus(); }, [otp.step]);

  if (otp.step === 'email') {
    return (
      <Form onSubmit={(e) => { e.preventDefault(); otp.requestCode(); }}>
        <Input
          ref={inputRef}
          type="email"
          autoComplete="email"
          placeholder={t('pinLogin.emailPlaceholder')}
          value={otp.email}
          onChange={(e) => otp.setEmail(e.target.value)}
          disabled={otp.busy}
          $error={!!otp.error}
        />
        {otp.error && <Note $tone="error">{otp.error}</Note>}
        <Button type="submit" disabled={otp.busy || !otp.email.includes('@')} $ready={otp.email.includes('@')}>
          {otp.busy ? t('pinLogin.sending') : t('pinLogin.sendCode')}
        </Button>
        <Note>{note || t('pinLogin.invitedOnly')}</Note>
      </Form>
    );
  }

  return (
    <Form onSubmit={(e) => { e.preventDefault(); otp.verifyCode(); }}>
      <Note>
        {t('pinLogin.codeSentTo')} <strong style={{ color: '#ccc' }}>{otp.email}</strong>{' '}
        <TextButton type="button" onClick={otp.changeEmail}>{t('pinLogin.changeEmail')}</TextButton>
      </Note>
      <Note>{t('pinLogin.codeIfInvited')}</Note>
      <Input
        ref={inputRef}
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        placeholder="••••••"
        value={otp.code}
        onChange={(e) => otp.setCode(e.target.value)}
        disabled={otp.busy}
        $code
        $error={!!otp.error}
      />
      {otp.error && <Note $tone="error">{otp.error}</Note>}
      {otp.notice && <Note $tone="ok">{otp.notice}</Note>}
      <Button type="submit" disabled={otp.busy || otp.code.length !== 6} $ready={otp.code.length === 6}>
        {otp.busy ? t('pinLogin.signingIn') : t('pinLogin.submit')}
      </Button>
      <Note>
        <TextButton type="button" disabled={otp.busy || otp.cooldown > 0} onClick={() => otp.requestCode(true)}>
          {otp.cooldown > 0 ? t('pinLogin.resendIn', { s: otp.cooldown }) : t('pinLogin.resend')}
        </TextButton>
      </Note>
    </Form>
  );
}
